import { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ArrowRight, ArrowLeft, ShieldCheck, CheckCircle2, Lock, Sparkles,
} from "lucide-react";
import { api } from "@/lib/apiClient";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const LOAN_PURPOSES = [
  "Debt consolidation","Home improvement","Medical / dental","Emergency expense",
  "Auto purchase","Auto refinance","Business use","Major purchase","Moving / relocation",
  "Wedding","Vacation","Other",
];

const PRODUCT_TITLES = {
  "personal":"Personal Loan","debt-consolidation":"Debt Consolidation","emergency":"Emergency Loan",
  "medical":"Medical Loan","home-improvement":"Home Improvement","auto":"Auto Loan","business":"Business Loan",
};

const STEP_LABELS = ["Loan","Personal","Contact","Employment","Bank","Financial & Sign"];

export default function Apply() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const initialProduct = sp.get("product") || "personal";
  const initialAmount = Number(sp.get("amount") || 15000);
  const initialTerm = Number(sp.get("term") || 60);

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [form, setForm] = useState({
    // Step 1
    product: initialProduct,
    loan_purpose: "",
    loan_amount: initialAmount,
    desired_term_months: initialTerm,
    funds_needed_by: "ASAP",
    payment_frequency: "Monthly",
    // Step 2 (NO SSN, NO DL)
    first_name: "",
    middle_name: "",
    last_name: "",
    dob: "",
    citizenship: "",
    marital_status: "",
    dependents: "",
    // Step 3
    email: "",
    mobile_phone: "",
    home_phone: "",
    address: "",
    apt: "",
    city: "",
    state: "",
    zip: "",
    time_at_address: "",
    housing_status: "",
    monthly_housing_payment: "",
    // Step 4
    employment_status: "",
    employer_name: "",
    employer_address: "",
    employer_phone: "",
    job_title: "",
    industry: "",
    employment_start_date: "",
    gross_monthly_income: "",
    net_monthly_income: "",
    additional_income: "",
    // Step 5 (NO routing/account)
    bank_name: "",
    account_type: "",
    direct_deposit: "",
    bank_account_years: "",
    // Step 6
    monthly_rent_or_mortgage: "",
    utilities: "",
    existing_loan_payments: "",
    credit_card_payments: "",
    child_support: "",
    total_monthly_expenses: "",
    bankruptcy_history: "",
    outstanding_loans: "",
    credit_score_range: "",
    // Consents
    certify_true: false,
    authorize_verify: false,
    authorize_credit_inquiry: false,
    consent_electronic_disclosures: false,
    agree_terms: false,
    acknowledge_disclosures: false,
    marketing_consent: false, // optional
    // E-sign
    esign_full_name: "",
    esign_date: new Date().toISOString().slice(0, 10),
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const progress = useMemo(() => (step / 6) * 100, [step]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const validateStep = () => {
    if (step === 1) {
      if (!form.loan_purpose) return "Please select a loan purpose.";
      if (!form.loan_amount || Number(form.loan_amount) < 500) return "Please enter a valid loan amount.";
      if (!form.desired_term_months) return "Please choose a desired term.";
    }
    if (step === 2) {
      if (!form.first_name || !form.last_name) return "Please enter your full legal name.";
      if (!form.dob) return "Please enter your date of birth.";
      const age = ageFromDob(form.dob);
      if (age < 18) return "You must be at least 18 years old to apply.";
      if (!form.citizenship) return "Please select your citizenship / residency status.";
    }
    if (step === 3) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
      if (!form.mobile_phone || form.mobile_phone.replace(/\D/g, "").length < 10)
        return "Please enter a valid 10-digit mobile phone.";
      if (!form.address || !form.city || !form.state || !/^\d{5}$/.test(form.zip))
        return "Please complete your residential address (5-digit ZIP required).";
      if (!form.housing_status) return "Please choose a housing status.";
    }
    if (step === 4) {
      if (!form.employment_status) return "Please select your employment status.";
      if (!form.gross_monthly_income) return "Please enter your gross monthly income.";
    }
    if (step === 5) {
      if (!form.bank_name) return "Please enter your bank name.";
      if (!form.account_type) return "Please select your account type.";
      const yrs = Number(form.bank_account_years);
      if (!Number.isFinite(yrs) || yrs < 0) return "Bank account age must be a positive number.";
    }
    if (step === 6) {
      if (!form.certify_true) return "Please certify that your information is accurate.";
      if (!form.authorize_verify) return "Please authorize verification of your information.";
      if (!form.authorize_credit_inquiry) return "Please authorize a credit inquiry.";
      if (!form.consent_electronic_disclosures) return "Please consent to electronic disclosures.";
      if (!form.agree_terms) return "Please agree to the Terms of Use and Privacy Policy.";
      if (!form.acknowledge_disclosures) return "Please acknowledge receipt of required disclosures.";
      if (!form.esign_full_name || form.esign_full_name.trim().split(" ").length < 2)
        return "Please type your full legal name for the electronic signature.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(6, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        loan_amount: Number(form.loan_amount) || 0,
        desired_term_months: Number(form.desired_term_months) || 0,
        dependents: form.dependents === "" ? null : Number(form.dependents),
        monthly_housing_payment: numOrNull(form.monthly_housing_payment),
        gross_monthly_income: numOrNull(form.gross_monthly_income),
        net_monthly_income: numOrNull(form.net_monthly_income),
        additional_income: numOrNull(form.additional_income),
        bank_account_years: numOrNull(form.bank_account_years),
        monthly_rent_or_mortgage: numOrNull(form.monthly_rent_or_mortgage),
        utilities: numOrNull(form.utilities),
        existing_loan_payments: numOrNull(form.existing_loan_payments),
        credit_card_payments: numOrNull(form.credit_card_payments),
        child_support: numOrNull(form.child_support),
        total_monthly_expenses: numOrNull(form.total_monthly_expenses),
        outstanding_loans: numOrNull(form.outstanding_loans),
      };
      const { data } = await api.post("/applications", payload);
      setConfirmation(data);
      toast.success("Application submitted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application. Please try again.");
    } finally { setBusy(false); }
  };

  if (confirmation) {
    return (
      <div className="text-white pt-24 pb-24 min-h-[70vh]">
        <div className="mx-auto max-w-2xl px-5 lg:px-8 text-center">
          <div className="h-16 w-16 rounded-full bg-[#17A673]/15 border border-[#17A673]/50 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-[#17A673]" />
          </div>
          <h1 className="mt-6 font-display text-4xl md:text-5xl font-bold">Application submitted.</h1>
          <p className="mt-4 text-white/70">
            Thanks {form.first_name}. Your reference is <span className="font-mono text-[#D4AF37]" data-testid="app-conf-id">{confirmation.application_id}</span>.
          </p>
          <p className="mt-3 text-white/60 max-w-lg mx-auto">
            A specialist will review your request and contact you within one business day at{" "}
            <span className="text-white">{form.email}</span> or{" "}
            <span className="text-white">{form.mobile_phone}</span>. No further action is required from you right now.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/"><Button className="ef-btn-primary rounded-full px-6 h-11" data-testid="app-conf-home">Back to home</Button></Link>
            <a href="tel:+17865989004"><Button variant="outline" className="ef-btn-ghost rounded-full px-6 h-11 bg-transparent text-white" data-testid="app-conf-call">Call a specialist</Button></a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-white pt-24 pb-24 ef-hero-radial">
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="ef-overline">Loan Application</div>
          <h1 className="font-display text-3xl md:text-5xl font-bold mt-3">
            Apply for a <em className="ef-gold-text not-italic">{PRODUCT_TITLES[form.product] || "Personal Loan"}</em>
          </h1>
          <div className="mt-4 flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest text-white/50">
            <div className="flex items-center gap-1.5"><Lock className="h-3 w-3 text-[#17A673]" /> 256-bit Encryption</div>
            <div className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-[#D4AF37]" /> No SSN or bank credentials required</div>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-between gap-2 text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">
            {STEP_LABELS.map((l, i) => (
              <div key={l} className={`${i + 1 === step ? "text-[#D4AF37]" : ""} ${i + 1 < step ? "text-white/70" : ""}`}>
                {i + 1}. {l}
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        {/* Card */}
        <div className="ef-card p-6 md:p-9" data-testid="apply-form">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.22 }}
            >
              {step === 1 && <StepLoan form={form} set={set} />}
              {step === 2 && <StepPersonal form={form} set={set} />}
              {step === 3 && <StepContact form={form} set={set} />}
              {step === 4 && <StepEmployment form={form} set={set} />}
              {step === 5 && <StepBank form={form} set={set} />}
              {step === 6 && <StepFinancial form={form} set={set} />}
            </motion.div>
          </AnimatePresence>

          {/* Nav buttons */}
          <div className="mt-9 flex items-center justify-between gap-3">
            <Button
              variant="outline"
              disabled={step === 1 || busy}
              onClick={prev}
              data-testid="apply-prev"
              className="ef-btn-ghost rounded-full px-5 h-11 bg-transparent text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>

            {step < 6 ? (
              <Button onClick={next} data-testid="apply-next" className="ef-btn-primary rounded-full px-6 h-11">
                Continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={busy} data-testid="apply-submit" className="ef-btn-primary rounded-full px-7 h-11">
                {busy ? "Submitting..." : <>Submit Application <Sparkles className="h-4 w-4 ml-2" /></>}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 text-[11px] text-white/40 leading-relaxed max-w-3xl">
          <ShieldCheck className="inline h-3 w-3 text-[#17A673] mr-1" />
          Elevate Financial is a lead-generation service and is not a bank or lender. This application is used to match you with licensed lending partners for underwriting review. Submission is not a credit application, credit offer, or commitment to lend. All final rates, terms, and eligibility are determined by the partner lender.
        </div>
      </div>
    </div>
  );
}

/* ---------------- Steps ---------------- */

function StepLoan({ form, set }) {
  return (
    <div className="space-y-5">
      <SectionHeader label="Step 1 · Loan Information" title="What do you need?" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Loan Purpose *">
          <Select value={form.loan_purpose} onValueChange={(v) => set({ loan_purpose: v })}>
            <SelectTrigger data-testid="apply-loan-purpose" className="ef-input"><SelectValue placeholder="Select purpose" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10 max-h-64">
              {LOAN_PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Loan Amount ($) *">
          <Input type="number" min="500" step="100" value={form.loan_amount}
            onChange={(e) => set({ loan_amount: e.target.value })} className="ef-input" data-testid="apply-loan-amount" />
        </Field>
        <Field label="Desired Term (months) *">
          <Select value={String(form.desired_term_months)} onValueChange={(v) => set({ desired_term_months: v })}>
            <SelectTrigger data-testid="apply-loan-term" className="ef-input"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {[12,24,36,48,60,72,84].map((t) => <SelectItem key={t} value={String(t)}>{t} months</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="When Funds Are Needed *">
          <Select value={form.funds_needed_by} onValueChange={(v) => set({ funds_needed_by: v })}>
            <SelectTrigger data-testid="apply-funds-needed" className="ef-input"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["ASAP","Within 1 week","Within 2 weeks","Within 30 days","No rush"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Preferred Payment Frequency *">
          <Select value={form.payment_frequency} onValueChange={(v) => set({ payment_frequency: v })}>
            <SelectTrigger data-testid="apply-pay-freq" className="ef-input"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["Monthly","Bi-weekly","Weekly"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}

function StepPersonal({ form, set }) {
  return (
    <div className="space-y-5">
      <SectionHeader label="Step 2 · Personal Information" title="Tell us about yourself" />
      <p className="text-xs text-white/50 -mt-2">We do not collect your Social Security number or driver's license on this site. Licensed partners collect any additional information through their own secure intake if you proceed.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="First Name *"><Input value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} className="ef-input" data-testid="apply-first-name" /></Field>
        <Field label="Middle Name (Optional)"><Input value={form.middle_name} onChange={(e) => set({ middle_name: e.target.value })} className="ef-input" data-testid="apply-middle-name" /></Field>
        <Field label="Last Name *"><Input value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} className="ef-input" data-testid="apply-last-name" /></Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Date of Birth *"><Input type="date" value={form.dob} onChange={(e) => set({ dob: e.target.value })} className="ef-input" data-testid="apply-dob" /></Field>
        <Field label="Citizenship / Residency *">
          <Select value={form.citizenship} onValueChange={(v) => set({ citizenship: v })}>
            <SelectTrigger data-testid="apply-citizenship" className="ef-input"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["U.S. Citizen","U.S. Permanent Resident","Visa Holder","Other"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Marital Status (Optional)">
          <Select value={form.marital_status} onValueChange={(v) => set({ marital_status: v })}>
            <SelectTrigger data-testid="apply-marital" className="ef-input"><SelectValue placeholder="Prefer not to say" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["Single","Married","Divorced","Widowed","Separated","Prefer not to say"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Number of Dependents (Optional)">
          <Input type="number" min="0" step="1" value={form.dependents}
            onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) set({ dependents: v }); }}
            className="ef-input" data-testid="apply-dependents" />
        </Field>
      </div>
    </div>
  );
}

function StepContact({ form, set }) {
  return (
    <div className="space-y-5">
      <SectionHeader label="Step 3 · Contact Information" title="How can we reach you?" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Email *"><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="ef-input" data-testid="apply-email" /></Field>
        <Field label="Mobile Phone *"><Input type="tel" placeholder="(555) 555-5555" value={form.mobile_phone} onChange={(e) => set({ mobile_phone: e.target.value })} className="ef-input" data-testid="apply-mobile" /></Field>
        <Field label="Home Phone (Optional)"><Input type="tel" value={form.home_phone} onChange={(e) => set({ home_phone: e.target.value })} className="ef-input" data-testid="apply-home-phone" /></Field>
      </div>
      <div className="grid sm:grid-cols-6 gap-4">
        <div className="sm:col-span-4"><Field label="Residential Address *"><Input value={form.address} onChange={(e) => set({ address: e.target.value })} className="ef-input" data-testid="apply-address" /></Field></div>
        <div className="sm:col-span-2"><Field label="Apt (Optional)"><Input value={form.apt} onChange={(e) => set({ apt: e.target.value })} className="ef-input" data-testid="apply-apt" /></Field></div>
      </div>
      <div className="grid sm:grid-cols-6 gap-4">
        <div className="sm:col-span-3"><Field label="City *"><Input value={form.city} onChange={(e) => set({ city: e.target.value })} className="ef-input" data-testid="apply-city" /></Field></div>
        <div className="sm:col-span-2">
          <Field label="State *">
            <Select value={form.state} onValueChange={(v) => set({ state: v })}>
              <SelectTrigger data-testid="apply-state" className="ef-input"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-[#0B1D3A] text-white border-white/10 max-h-64">
                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="sm:col-span-1"><Field label="ZIP *"><Input inputMode="numeric" maxLength={5} value={form.zip} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 5); set({ zip: v }); }} className="ef-input" data-testid="apply-zip" /></Field></div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Time at Current Address">
          <Select value={form.time_at_address} onValueChange={(v) => set({ time_at_address: v })}>
            <SelectTrigger data-testid="apply-time-address" className="ef-input"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["Less than 6 months","6–12 months","1–2 years","2–5 years","5+ years"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Housing Status *">
          <Select value={form.housing_status} onValueChange={(v) => set({ housing_status: v })}>
            <SelectTrigger data-testid="apply-housing" className="ef-input"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["Own","Rent","Living with family","Other"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Monthly Housing Payment ($)">
          <Input type="number" min="0" value={form.monthly_housing_payment}
            onChange={(e) => set({ monthly_housing_payment: e.target.value })} className="ef-input" data-testid="apply-housing-payment" />
        </Field>
      </div>
    </div>
  );
}

function StepEmployment({ form, set }) {
  return (
    <div className="space-y-5">
      <SectionHeader label="Step 4 · Employment & Income" title="Your work and income" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Employment Status *">
          <Select value={form.employment_status} onValueChange={(v) => set({ employment_status: v })}>
            <SelectTrigger data-testid="apply-emp-status" className="ef-input"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["Full-time (W-2)","Part-time (W-2)","Self-employed","Contractor / 1099","Retired","Unemployed","Other"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Employment Start Date">
          <Input type="date" value={form.employment_start_date} onChange={(e) => set({ employment_start_date: e.target.value })} className="ef-input" data-testid="apply-emp-start" />
        </Field>
        <Field label="Employer Name"><Input value={form.employer_name} onChange={(e) => set({ employer_name: e.target.value })} className="ef-input" data-testid="apply-employer" /></Field>
        <Field label="Job Title"><Input value={form.job_title} onChange={(e) => set({ job_title: e.target.value })} className="ef-input" data-testid="apply-job-title" /></Field>
        <Field label="Industry"><Input value={form.industry} onChange={(e) => set({ industry: e.target.value })} className="ef-input" data-testid="apply-industry" /></Field>
        <Field label="Employer Phone"><Input type="tel" value={form.employer_phone} onChange={(e) => set({ employer_phone: e.target.value })} className="ef-input" data-testid="apply-employer-phone" /></Field>
        <div className="sm:col-span-2"><Field label="Employer Address"><Input value={form.employer_address} onChange={(e) => set({ employer_address: e.target.value })} className="ef-input" data-testid="apply-employer-address" /></Field></div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Gross Monthly Income ($) *">
          <Input type="number" min="0" value={form.gross_monthly_income} onChange={(e) => set({ gross_monthly_income: e.target.value })} className="ef-input" data-testid="apply-gross" />
        </Field>
        <Field label="Net Monthly Income ($)">
          <Input type="number" min="0" value={form.net_monthly_income} onChange={(e) => set({ net_monthly_income: e.target.value })} className="ef-input" data-testid="apply-net" />
        </Field>
        <Field label="Additional Income (Optional)">
          <Input type="number" min="0" value={form.additional_income} onChange={(e) => set({ additional_income: e.target.value })} className="ef-input" data-testid="apply-additional-income" />
        </Field>
      </div>
    </div>
  );
}

function StepBank({ form, set }) {
  return (
    <div className="space-y-5">
      <SectionHeader label="Step 5 · Bank Information" title="Where should partners route funds?" />
      <div className="rounded-xl bg-[#1B63F0]/10 border border-[#1B63F0]/30 p-4 flex items-start gap-3">
        <ShieldCheck className="h-4 w-4 text-[#1B63F0] mt-0.5 shrink-0" />
        <p className="text-xs text-white/70 leading-relaxed">
          For your security, we <strong>do not collect</strong> your bank routing or account numbers on this site. If a licensed partner moves forward with your application, they will collect that information directly through their own compliant intake.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Bank Name *"><Input placeholder="e.g., Chase, Wells Fargo" value={form.bank_name} onChange={(e) => set({ bank_name: e.target.value })} className="ef-input" data-testid="apply-bank-name" /></Field>
        <Field label="Account Type *">
          <Select value={form.account_type} onValueChange={(v) => set({ account_type: v })}>
            <SelectTrigger data-testid="apply-account-type" className="ef-input"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["Checking","Savings","Both"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Direct Deposit Status">
          <Select value={form.direct_deposit} onValueChange={(v) => set({ direct_deposit: v })}>
            <SelectTrigger data-testid="apply-direct-deposit" className="ef-input"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["Yes — my income is direct-deposited","No"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Years Holding This Bank Account *">
          <Input type="number" min="0" step="1" placeholder="Number of years"
            value={form.bank_account_years}
            onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) set({ bank_account_years: v }); }}
            className="ef-input" data-testid="apply-bank-years" />
        </Field>
      </div>
    </div>
  );
}

function StepFinancial({ form, set }) {
  return (
    <div className="space-y-6">
      <SectionHeader label="Step 6 · Financial Information & Signature" title="A few final details" />
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Monthly Rent / Mortgage ($)"><Input type="number" min="0" value={form.monthly_rent_or_mortgage} onChange={(e) => set({ monthly_rent_or_mortgage: e.target.value })} className="ef-input" data-testid="apply-rent" /></Field>
        <Field label="Utilities ($)"><Input type="number" min="0" value={form.utilities} onChange={(e) => set({ utilities: e.target.value })} className="ef-input" data-testid="apply-utilities" /></Field>
        <Field label="Existing Loan Payments ($)"><Input type="number" min="0" value={form.existing_loan_payments} onChange={(e) => set({ existing_loan_payments: e.target.value })} className="ef-input" data-testid="apply-existing-loans" /></Field>
        <Field label="Credit Card Payments ($)"><Input type="number" min="0" value={form.credit_card_payments} onChange={(e) => set({ credit_card_payments: e.target.value })} className="ef-input" data-testid="apply-cc" /></Field>
        <Field label="Child Support ($, Optional)"><Input type="number" min="0" value={form.child_support} onChange={(e) => set({ child_support: e.target.value })} className="ef-input" data-testid="apply-child" /></Field>
        <Field label="Total Monthly Expenses ($)"><Input type="number" min="0" value={form.total_monthly_expenses} onChange={(e) => set({ total_monthly_expenses: e.target.value })} className="ef-input" data-testid="apply-total-exp" /></Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Bankruptcy in Last 7 Years">
          <Select value={form.bankruptcy_history} onValueChange={(v) => set({ bankruptcy_history: v })}>
            <SelectTrigger data-testid="apply-bankruptcy" className="ef-input"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["No","Yes — discharged","Yes — active","Prefer not to say"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Outstanding Loans ($)">
          <Input type="number" min="0" value={form.outstanding_loans} onChange={(e) => set({ outstanding_loans: e.target.value })} className="ef-input" data-testid="apply-outstanding" />
        </Field>
        <Field label="Estimated Credit Score (Optional)">
          <Select value={form.credit_score_range} onValueChange={(v) => set({ credit_score_range: v })}>
            <SelectTrigger data-testid="apply-credit-range" className="ef-input"><SelectValue placeholder="Prefer not to say" /></SelectTrigger>
            <SelectContent className="bg-[#0B1D3A] text-white border-white/10">
              {["Excellent (720+)","Good (680–719)","Fair (620–679)","Below 620","I don't know","Prefer not to say"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {/* Consents */}
      <div className="mt-2 pt-6 border-t border-white/10">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-3">Applicant Consent (Required)</div>
        <div className="space-y-3">
          <Consent testid="c1" checked={form.certify_true} onChange={(v) => set({ certify_true: v })}>
            I certify that the information I have provided is true, accurate, and complete to the best of my knowledge.
          </Consent>
          <Consent testid="c2" checked={form.authorize_verify} onChange={(v) => set({ authorize_verify: v })}>
            I authorize Elevate Financial and its lending partners to verify the information I have provided, including through third-party consumer reporting sources.
          </Consent>
          <Consent testid="c3" checked={form.authorize_credit_inquiry} onChange={(v) => set({ authorize_credit_inquiry: v })}>
            I authorize a credit inquiry where permitted by applicable law. An initial inquiry may be a soft pull that does not affect my credit score. A hard inquiry may be required by a partner before extending an offer.
          </Consent>
          <Consent testid="c4" checked={form.consent_electronic_disclosures} onChange={(v) => set({ consent_electronic_disclosures: v })}>
            I consent to receive required disclosures, agreements, and notices electronically (E-SIGN Act) at the email address I provided.
          </Consent>
          <Consent testid="c5" checked={form.agree_terms} onChange={(v) => set({ agree_terms: v })}>
            I agree to the <a href="/legal/terms" className="text-[#D4AF37] hover:underline">Terms of Use</a> and <a href="/legal/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</a>.
          </Consent>
          <Consent testid="c6" checked={form.acknowledge_disclosures} onChange={(v) => set({ acknowledge_disclosures: v })}>
            I acknowledge receipt of required federal and state disclosures, including the Equal Credit Opportunity Act (ECOA) notice, and the notice that Elevate Financial is not a bank or lender.
          </Consent>
          {/* Optional marketing (NOT pre-selected) */}
          <Consent testid="c7-marketing" checked={form.marketing_consent} onChange={(v) => set({ marketing_consent: v })} optional>
            <span className="text-white/60">(Optional)</span> I would like to receive occasional promotional communications from Elevate Financial about loan products and services. I can unsubscribe anytime.
          </Consent>
        </div>
      </div>

      {/* E-Signature */}
      <div className="mt-2 pt-6 border-t border-white/10">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37] mb-3">Electronic Signature</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full Legal Name *">
            <Input placeholder="Type your full legal name" value={form.esign_full_name}
              onChange={(e) => set({ esign_full_name: e.target.value })}
              className="ef-input italic" style={{ fontFamily: "Georgia, serif" }}
              data-testid="apply-esign-name" />
          </Field>
          <Field label="Date">
            <Input type="date" value={form.esign_date} onChange={(e) => set({ esign_date: e.target.value })} className="ef-input" data-testid="apply-esign-date" />
          </Field>
        </div>
        <p className="mt-3 text-[10px] text-white/40 leading-relaxed">
          By typing your name above, you agree that this electronic signature is the legal equivalent of your manual signature. Timestamp and IP address will be recorded for compliance purposes.
        </p>
      </div>
    </div>
  );
}

/* ---------------- Bits ---------------- */

function SectionHeader({ label, title }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">{label}</div>
      <div className="font-display text-2xl md:text-3xl font-bold mt-1">{title}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Consent({ children, checked, onChange, testid, optional = false }) {
  return (
    <label className="flex items-start gap-2.5 text-[12px] text-white/75 leading-relaxed cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testid} className="border-white/25 mt-0.5" />
      <span>{children}</span>
    </label>
  );
}

/* ---------------- Utils ---------------- */

function ageFromDob(dob) {
  if (!dob) return 0;
  const d = new Date(dob);
  if (isNaN(d)) return 0;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a;
}
function numOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
